import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button as AriaButton, Link } from 'react-aria-components';

import { ShortcutsHelp } from '../ShortcutsHelp/ShortcutsHelp.tsx';
import styles from './AppShell.module.css';
import { NavIcon, type NavIconName } from './NavIcon.tsx';

export type { NavIconName } from './NavIcon.tsx';

export interface NavItem {
  href: string;
  label: string;
  /** A small line icon shown beside the label, and alone when the sidebar is collapsed. */
  icon?: NavIconName;
  /**
   * The group the item sits under, shown as a small grey label ("Front desk", "Clinical").
   * Consecutive items with the same group are listed together.
   */
  group?: string;
}

export interface AppShellProps {
  /** Items for the current role only; most roles see seven or fewer. */
  navItems: readonly NavItem[];
  /** Pinned to the bottom of the nav, e.g. Settings. */
  footerNavItems?: readonly NavItem[];
  /** The path of the current page, to mark the active nav item. */
  currentPath: string;
  /** Facility or branch switcher, on the right of the header. */
  facility: ReactNode;
  /**
   * Global patient search trigger (Ctrl/⌘K). It sits at the top of the sidebar on wide
   * screens, and in the header when the sidebar is collapsed or folded away.
   */
  search?: ReactNode;
  /** Account menu, at the far right of the header. */
  account: ReactNode;
  /** Full-width strip under the header, e.g. the offline notice. */
  statusStrip?: ReactNode;
  /** The shell's own words, for another interface language. English by default. */
  labels?: Partial<AppShellLabels>;
  /**
   * Gives a page title in the interface language. Page titles are written in English; the
   * app maps the ones it has words for (docs/plans/phase3-portal-feedback-kiswahili.md §8).
   */
  translateTitle?: (title: string) => string;
  children: ReactNode;
}

/** The words the shell itself shows or reads out. */
export interface AppShellLabels {
  skipToContent: string;
  menu: string;
  closeMenu: string;
  expandSidebar: string;
  collapseSidebar: string;
  mainNavigation: string;
}

const ENGLISH_LABELS: AppShellLabels = {
  skipToContent: 'Skip to content',
  menu: 'Menu',
  closeMenu: 'Close menu',
  expandSidebar: 'Expand sidebar',
  collapseSidebar: 'Collapse sidebar',
  mainNavigation: 'Main',
};

/**
 * Where PageHeader puts the page title: the header's slot inside a shell (null until it
 * mounts), or undefined outside a shell, where the title renders in place.
 */
const TitleSlotContext = createContext<HTMLElement | null | undefined>(undefined);

/** How page titles are shown in the interface language; as written when there is no shell. */
const TitleTranslationContext = createContext<(title: string) => string>((title) => title);

const COLLAPSED_KEY = 'wodi.nav.collapsed';
const WIDE_QUERY = '(min-width: 1024px)';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function saveCollapsed(collapsed: boolean) {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    // A convenience only: without storage the sidebar simply opens expanded next time.
  }
}

function subscribeWide(onChange: () => void): () => void {
  if (typeof window.matchMedia !== 'function') return () => undefined;
  const query = window.matchMedia(WIDE_QUERY);
  query.addEventListener('change', onChange);
  return () => {
    query.removeEventListener('change', onChange);
  };
}

function isWide(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(WIDE_QUERY).matches;
}

/** True from 1024px, where the full sidebar is shown. False where matchMedia is missing. */
function useWideScreen(): boolean {
  return useSyncExternalStore(subscribeWide, isWide, () => false);
}

function isCurrent(itemHref: string, currentPath: string): boolean {
  if (itemHref === '/') return currentPath === '/';
  return currentPath === itemHref || currentPath.startsWith(`${itemHref}/`);
}

interface NavGroup {
  label: string | undefined;
  items: readonly NavItem[];
}

/** Groups consecutive items that share a group label, keeping their order. */
function groupItems(items: readonly NavItem[]): NavGroup[] {
  return items.reduce<NavGroup[]>((groups, item) => {
    const last = groups.at(-1);
    if (last !== undefined && last.label === item.group) {
      return [...groups.slice(0, -1), { label: last.label, items: [...last.items, item] }];
    }
    return [...groups, { label: item.group, items: [item] }];
  }, []);
}

function NavList({
  items,
  currentPath,
  onNavigate,
  labelledBy,
}: {
  items: readonly NavItem[];
  currentPath: string;
  onNavigate: () => void;
  labelledBy: string | undefined;
}) {
  return (
    <ul className={styles.navList} aria-labelledby={labelledBy}>
      {items.map((item) => {
        const current = isCurrent(item.href, currentPath);
        return (
          <li key={item.href}>
            <Link
              href={item.href}
              className={styles.navLink}
              aria-current={current ? 'page' : undefined}
              onPress={onNavigate}
            >
              {item.icon === undefined ? null : (
                <NavIcon name={item.icon} className={styles.navIcon} />
              )}
              <span className={styles.navLabel}>{item.label}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function NavGroups({
  items,
  currentPath,
  onNavigate,
}: {
  items: readonly NavItem[];
  currentPath: string;
  onNavigate: () => void;
}) {
  const baseId = useId();
  return groupItems(items).map((group, index) => {
    const labelId = group.label === undefined ? undefined : `${baseId}-${String(index)}`;
    return (
      <div key={`${String(index)}-${group.label ?? ''}`} className={styles.navGroup}>
        {labelId === undefined ? null : (
          <span id={labelId} className={styles.groupLabel}>
            {group.label}
          </span>
        )}
        <NavList
          items={group.items}
          currentPath={currentPath}
          onNavigate={onNavigate}
          labelledBy={labelId}
        />
      </div>
    );
  });
}

/**
 * The app frame from DESIGN.md §6.1 (ADR 0019): a light grey sidebar with the app name,
 * search and grouped nav; a white header with the page title on the left and the facility
 * and account on the right; and the page in a soft grey inset panel that fills the width.
 *
 * - From 1024px the sidebar is shown; its collapse button folds it to an icon rail
 *   (remembered on this device).
 * - From 640px to 1023px it is a 72px rail with a Menu button.
 * - Under 640px (phones) there is no rail: Menu sits at the left of the header.
 *
 * When folded away, Menu opens the nav as a floating sheet over a plain scrim. It closes on
 * Escape, a click outside, the Close button or choosing a page, and focus returns to Menu.
 * "?" anywhere outside a field opens the list of keyboard shortcuts.
 */
export function AppShell({
  navItems,
  footerNavItems = [],
  currentPath,
  facility,
  search,
  account,
  statusStrip,
  labels,
  translateTitle,
  children,
}: AppShellProps) {
  const words = { ...ENGLISH_LABELS, ...labels };
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null);
  const wide = useWideScreen();
  const navId = useId();
  const navRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  // With the full sidebar on a wide screen, search sits at its top; otherwise in the header.
  const searchInSidebar = wide && !collapsed;

  const closeMenu = (returnFocus: boolean) => {
    setMenuOpen(false);
    if (returnFocus) menuButtonRef.current?.focus();
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    // Start on the current page, so Tab and arrow keys begin where the person is.
    const nav = navRef.current;
    const target =
      nav?.querySelector<HTMLElement>('[aria-current="page"]') ??
      nav?.querySelector<HTMLElement>('a[href]');
    target?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const onNavigate = () => {
    closeMenu(false);
  };

  return (
    <div className={styles.shell} data-collapsed={collapsed || undefined}>
      <a className={styles.skipLink} href="#main-content">
        {words.skipToContent}
      </a>
      <aside className={styles.rail} data-open={menuOpen || undefined}>
        <div className={styles.brandRow}>
          <span className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              W
            </span>
            <span className={styles.brandName}>Wodi</span>
          </span>
          <AriaButton
            className={styles.collapseButton}
            aria-label={collapsed ? words.expandSidebar : words.collapseSidebar}
            aria-pressed={collapsed}
            onPress={() => {
              saveCollapsed(!collapsed);
              setCollapsed(!collapsed);
            }}
          >
            <NavIcon name="sidebar" />
          </AriaButton>
        </div>
        <AriaButton
          ref={menuButtonRef}
          className={styles.menuButton}
          aria-expanded={menuOpen}
          aria-controls={navId}
          onPress={() => {
            setMenuOpen((open) => !open);
          }}
        >
          <span className={styles.menuIcon} aria-hidden="true" />
          {words.menu}
        </AriaButton>
        {searchInSidebar && search !== undefined ? (
          <div className={styles.sidebarSearch}>{search}</div>
        ) : null}
        {menuOpen ? (
          <div
            className={styles.scrim}
            aria-hidden="true"
            onClick={() => {
              closeMenu(true);
            }}
          />
        ) : null}
        <nav
          ref={navRef}
          id={navId}
          aria-label={words.mainNavigation}
          className={styles.nav}
          data-open={menuOpen || undefined}
        >
          {menuOpen ? (
            <div className={styles.sheetHead}>
              <span className={styles.sheetBrand}>Wodi</span>
              <AriaButton
                className={styles.closeButton}
                onPress={() => {
                  closeMenu(true);
                }}
              >
                {words.closeMenu}
              </AriaButton>
            </div>
          ) : null}
          <NavGroups items={navItems} currentPath={currentPath} onNavigate={onNavigate} />
          {footerNavItems.length > 0 ? (
            <div className={styles.navFooter}>
              <NavGroups items={footerNavItems} currentPath={currentPath} onNavigate={onNavigate} />
            </div>
          ) : null}
        </nav>
      </aside>
      <div className={styles.main}>
        <header className={styles.topBar}>
          <div ref={setTitleSlot} className={styles.titleSlot} />
          <div className={styles.topBarEnd}>
            {searchInSidebar || search === undefined ? null : (
              <div className={styles.search}>{search}</div>
            )}
            <div className={styles.facility}>{facility}</div>
            <span className={styles.divider} aria-hidden="true" />
            <div className={styles.account}>{account}</div>
          </div>
        </header>
        {statusStrip}
        <main id="main-content" tabIndex={-1} className={styles.content}>
          <TitleSlotContext.Provider value={titleSlot}>
            <TitleTranslationContext.Provider value={translateTitle ?? ((title) => title)}>
              {children}
            </TitleTranslationContext.Provider>
          </TitleSlotContext.Provider>
        </main>
      </div>
      <ShortcutsHelp />
    </div>
  );
}

/** Full-width dark strip under the header (DESIGN.md §7, offline indicator). */
export function StatusStrip({ children }: { children: string }) {
  return (
    <div role="status" className={styles.statusStrip}>
      {children}
    </div>
  );
}

export interface PageHeaderProps {
  title: string;
  /** One sentence under the title saying what this page is for. Optional. */
  description?: string;
  /** The page's actions: at most one primary. They wrap under the title on narrow screens. */
  actions?: ReactNode;
}

/**
 * One page title per page, at t-display (t-title on phones). Inside AppShell it is shown in
 * the white header above the content panel (ADR 0019); outside a shell, in place.
 */
export function PageHeader({ title, description, actions }: PageHeaderProps) {
  const slot = useContext(TitleSlotContext);
  const shown = useContext(TitleTranslationContext)(title);
  const header = (
    <div className={styles.pageHeader} data-in-shell={slot === undefined ? undefined : true}>
      <div className={styles.pageHeading}>
        <h1 className={styles.pageTitle}>{shown}</h1>
        {description === undefined ? null : <p className={styles.pageDescription}>{description}</p>}
      </div>
      {actions === undefined ? null : <div className={styles.pageActions}>{actions}</div>}
    </div>
  );
  if (slot === undefined) return header;
  // The header slot mounts with the shell; until that commit there is nowhere to put it.
  if (slot === null) return null;
  return createPortal(header, slot);
}

/** A sentence saying what goes here, and optionally the one action to start. No illustrations. */
export function EmptyState({ children, action }: { children: string; action?: ReactNode }) {
  return (
    <div className={styles.emptyState}>
      <p>{children}</p>
      {action}
    </div>
  );
}
